import { useEffect, useRef, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { MessageCircle, ShieldCheck, ExternalLink, CheckCircle2, Loader2 } from 'lucide-react';
import HelpTip from '@/components/HelpTip';

// B2: Embedded Signup self-service. El tenant conecta su WhatsApp autorizando a Gupshup
// en SU propio Meta (popup oficial), sin darnos acceso ni credenciales.
export default function EmbeddedSignupCard({ onConnected }) {
  const [status, setStatus] = useState('idle'); // idle | starting | waiting | connected
  const [link, setLink] = useState('');
  const [appId, setAppId] = useState('');
  const [phone, setPhone] = useState('');
  const [needsKey, setNeedsKey] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const start = async () => {
    setStatus('starting');
    try {
      const { data } = await api.post('/whatsapp/gupshup/onboard/start', {});
      setLink(data.link); setAppId(data.app_id); setStatus('waiting');
      const win = window.open(data.link, '_blank', 'noopener');
      if (!win) toast.info('Abre el enlace de conexión que aparece abajo para continuar.');
      pollRef.current = setInterval(() => checkStatus(data.app_id), 5000);
    } catch (e) {
      setStatus('idle');
      toast.error(e.response?.data?.detail || 'No se pudo iniciar la conexión');
    }
  };

  const checkStatus = async (id) => {
    try {
      const { data } = await api.get('/whatsapp/gupshup/onboard/status', { params: { app_id: id } });
      if (data.status === 'connected') {
        if (pollRef.current) clearInterval(pollRef.current);
        setPhone(data.phone || ''); setNeedsKey(Boolean(data.needs_send_key)); setStatus('connected');
        toast.success('¡WhatsApp conectado!');
        if (onConnected) onConnected();
      }
    } catch { /* sigue esperando */ }
  };

  return (
    <Card className="bg-gradient-to-br from-emerald-950/40 to-zinc-950/70 border-emerald-500/20" data-testid="embedded-signup-card">
      <CardHeader>
        <CardTitle className="text-white text-base flex items-center gap-2">
          <MessageCircle className="w-5 h-5 text-emerald-400 shrink-0" />Conecta tu WhatsApp en 1 minuto
          <HelpTip title="Conexión segura y directa" content="Te abrimos una ventana OFICIAL de Meta donde inicias sesión con TU cuenta y autorizas la conexión. No nos das tu contraseña ni acceso a tu cuenta — todo lo apruebas tú en tu propio Meta Business. Al terminar, tu número queda conectado automáticamente." />
        </CardTitle>
        <CardDescription className="text-xs">La forma más fácil: sin compartir contraseñas ni dar acceso a tu Meta. Tú autorizas en una ventana oficial de Meta.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {status === 'idle' && (
          <>
            <details className="rounded-lg border border-white/10 bg-black/20 p-3" data-testid="es-guide">
              <summary className="text-xs font-semibold text-emerald-300 cursor-pointer select-none">¿Qué va a pasar?</summary>
              <ol className="mt-2 space-y-1 list-decimal list-inside text-[11px] text-zinc-300 leading-relaxed">
                <li>Se abre una <b>ventana oficial de Meta</b>.</li>
                <li>Inicias sesión con <b>tu</b> cuenta de Facebook/Meta Business.</li>
                <li>Eliges (o creas) tu número de WhatsApp y <b>autorizas la conexión</b>.</li>
                <li>Listo — tu número queda conectado aquí, sin pasos técnicos.</li>
              </ol>
              <p className="mt-2 text-[10px] text-zinc-500">Requisitos: ser administrador de tu Meta Business y un número que NO esté ya activo en otra app de WhatsApp.</p>
            </details>
            <Button onClick={start} className="bg-emerald-600 hover:bg-emerald-500 gap-2" data-testid="es-connect-btn">
              <ShieldCheck className="w-4 h-4" />Conectar mi WhatsApp
            </Button>
          </>
        )}

        {status === 'starting' && (
          <div className="flex items-center gap-2 text-sm text-zinc-300"><Loader2 className="w-4 h-4 animate-spin" />Preparando tu conexión segura…</div>
        )}

        {status === 'waiting' && (
          <div className="space-y-2" data-testid="es-waiting">
            <div className="flex items-center gap-2 text-sm text-emerald-300"><Loader2 className="w-4 h-4 animate-spin" />Esperando a que completes la conexión en la ventana de Meta…</div>
            <p className="text-[11px] text-zinc-400">Si no se abrió la ventana, ábrela aquí:</p>
            <a href={link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-emerald-300 underline break-all" data-testid="es-link"><ExternalLink className="w-3.5 h-3.5" />Abrir ventana de conexión</a>
            <p className="text-[10px] text-zinc-500">Esta pantalla se actualiza sola cuando termines. Puedes dejarla abierta.</p>
          </div>
        )}

        {status === 'connected' && (
          <div className="space-y-2" data-testid="es-connected">
            <div className="flex items-center gap-2 text-sm text-emerald-300"><CheckCircle2 className="w-4 h-4" />¡Conectado! Tu número {phone && <span className="font-mono">{phone}</span>} ya está ligado a tu cuenta.</div>
            {needsKey && <p className="text-[11px] text-amber-300">Ya puedes RECIBIR mensajes. Para ENVIAR, el equipo de LeadCEM activará el último paso (la llave de envío) — puede tardar unos minutos.</p>}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
